import React, { useState } from 'react';
import { Button } from './Button';
import { AuthService } from '../services/AuthService';

const authService = new AuthService();

interface Props {
  open: boolean;
  onClose: () => void;
}

export const RegisterModal: React.FC<Props> = ({ open, onClose }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'USER' | 'ADMIN'>('USER');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const passwordValid = password.length === 0 || password.length >= 8;

  const reset = () => {
    setUsername('');
    setPassword('');
    setRole('USER');
    setError(null);
    setSuccessMsg(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await authService.register(username, password, role);
      setSuccessMsg(`Benutzer "${username}" erfolgreich angelegt`);
      setUsername('');
      setPassword('');
      setRole('USER');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-sm mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold">Benutzer anlegen</h3>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="label" htmlFor="rm-username">Benutzername</label>
            <input
              id="rm-username"
              type="text"
              className="input"
              value={username}
              onChange={e => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="rm-password">
              Passwort <span className="text-slate-400 font-normal">(min. 8 Zeichen)</span>
            </label>
            <input
              id="rm-password"
              type="password"
              className={`input ${!passwordValid ? 'border-red-400 focus:border-red-400' : ''}`}
              value={password}
              onChange={e => setPassword(e.target.value)}
              disabled={loading}
            />
            {!passwordValid && (
              <p className="text-xs text-red-500 mt-1">Mindestens 8 Zeichen erforderlich</p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="rm-role">Rolle</label>
            <select
              id="rm-role"
              className="input"
              value={role}
              onChange={e => setRole(e.target.value as 'USER' | 'ADMIN')}
              disabled={loading}
            >
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
            </select>
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}
          {successMsg && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">{successMsg}</p>
          )}
          <div className="flex justify-end gap-2 mt-1">
            <Button variant="secondary" type="button" onClick={handleClose}>Schließen</Button>
            <Button
              variant="primary"
              type="submit"
              loading={loading}
              disabled={!username || password.length < 8}
            >
              Anlegen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
